import React, { useState, useEffect } from 'react';
import { maintenanceService, featureFlagsService } from '../services/api';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Truck, 
  Wrench, 
  AlertTriangle,
  RefreshCw,
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  FileText
} from 'lucide-react';

const MaintenanceDashboard = () => {
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [issues, setIssues] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [vehicleForm, setVehicleForm] = useState({
    unit_number: '',
    vehicle_type: 'truck',
    vin: '',
    make: '',
    model: '',
    year: '',
    license_plate: '',
    license_state: ''
  });

  useEffect(() => {
    checkFeatureAndLoad();
  }, []);

  const checkFeatureAndLoad = async () => {
    try {
      const enabledFeatures = await featureFlagsService.getEnabledFeatures();
      const isEnabled = enabledFeatures.maintenance_module_enabled === true;
      setFeatureEnabled(isEnabled);
      
      if (isEnabled) {
        await loadData();
      }
    } catch (err) {
      setFeatureEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    try {
      const [dashboardData, vehiclesData, issuesData] = await Promise.all([
        maintenanceService.getDashboard().catch(() => null),
        maintenanceService.getVehicles().catch(() => ({ vehicles: [] })),
        maintenanceService.getIssues().catch(() => ({ issues: [] }))
      ]);
      
      setDashboard(dashboardData);
      setVehicles(vehiclesData.vehicles || []);
      setIssues(issuesData.issues || []);
    } catch (err) {
      console.error('Failed to load maintenance data:', err);
    }
  };

  const handleAddVehicle = async (e) => {
    e.preventDefault();
    try {
      await maintenanceService.createVehicle({
        ...vehicleForm,
        year: vehicleForm.year ? parseInt(vehicleForm.year) : null
      });
      alert('Vehicle added successfully!');
      setShowAddVehicleModal(false);
      setVehicleForm({
        unit_number: '',
        vehicle_type: 'truck',
        vin: '',
        make: '',
        model: '',
        year: '',
        license_plate: '',
        license_state: ''
      });
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add vehicle');
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      maintenance: { color: 'bg-yellow-100 text-yellow-800', icon: Wrench },
      out_of_service: { color: 'bg-red-100 text-red-800', icon: XCircle },
      retired: { color: 'bg-gray-100 text-gray-800', icon: Clock }
    };
    const { color, icon: Icon } = config[status] || config.active;
    return (
      <Badge className={`${color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status?.replace(/_/g, ' ').toUpperCase()}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-orange-100 text-orange-800',
      critical: 'bg-red-100 text-red-800'
    };
    return <Badge className={colors[priority] || colors.low}>{priority?.toUpperCase()}</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!featureEnabled) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto p-6">
          <Card className="border-yellow-200 bg-yellow-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-8 h-8 text-yellow-600 flex-shrink-0" />
                <div>
                  <h2 className="text-xl font-semibold text-yellow-800">Feature Not Enabled</h2>
                  <p className="text-yellow-700 mt-2">
                    The Maintenance Module is currently disabled. Contact your administrator to enable this feature.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Maintenance Dashboard</h1>
            <p className="text-gray-600 mt-1">Vehicle management and issue tracking</p>
          </div>
          <Button onClick={() => setShowAddVehicleModal(true)} className="flex items-center gap-2">
            <Plus className="w-4 h-4" /> Add Vehicle
          </Button>
        </div>

        {/* Stats */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Truck className="w-8 h-8 text-blue-600" />
                  <div>
                    <div className="text-2xl font-bold">{dashboard.vehicle_stats?.total || 0}</div>
                    <div className="text-sm text-gray-500">Total Vehicles</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                  <div>
                    <div className="text-2xl font-bold">{dashboard.vehicle_stats?.active || 0}</div>
                    <div className="text-sm text-gray-500">Active</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <Wrench className="w-8 h-8 text-yellow-600" />
                  <div>
                    <div className="text-2xl font-bold">{dashboard.vehicle_stats?.maintenance || 0}</div>
                    <div className="text-sm text-gray-500">In Maintenance</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className={dashboard.vehicle_stats?.out_of_service > 0 ? 'border-red-200 bg-red-50' : ''}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <XCircle className={`w-8 h-8 ${dashboard.vehicle_stats?.out_of_service > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                  <div>
                    <div className={`text-2xl font-bold ${dashboard.vehicle_stats?.out_of_service > 0 ? 'text-red-600' : ''}`}>
                      {dashboard.vehicle_stats?.out_of_service || 0}
                    </div>
                    <div className="text-sm text-gray-500">Out of Service</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Issues Alert */}
        {dashboard?.issue_stats?.critical_open > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-red-800">Critical Issues Open</div>
              <div className="text-sm text-red-700">
                {dashboard.issue_stats.critical_open} critical issue(s) require immediate attention.
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b mb-6 overflow-x-auto">
          {['overview', 'vehicles', 'issues'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium capitalize whitespace-nowrap ${
                activeTab === tab 
                  ? 'text-blue-600 border-b-2 border-blue-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Recent Issues</CardTitle>
              </CardHeader>
              <CardContent>
                {(dashboard?.recent_issues || []).length === 0 ? (
                  <div className="text-center py-4 text-gray-500">No recent issues</div>
                ) : (
                  <div className="space-y-3">
                    {(dashboard?.recent_issues || []).map(issue => (
                      <div key={issue.id} className="flex justify-between items-start py-2 border-b last:border-0">
                        <div>
                          <div className="font-medium text-sm">{issue.title}</div>
                          <div className="text-xs text-gray-500">Unit: {issue.vehicle_unit_number}</div>
                        </div>
                        {getPriorityBadge(issue.priority)}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Recent Inspections</CardTitle>
              </CardHeader>
              <CardContent>
                {(dashboard?.recent_inspections || []).length === 0 ? (
                  <div className="text-center py-4 text-gray-500">No recent inspections</div>
                ) : (
                  <div className="space-y-3">
                    {(dashboard?.recent_inspections || []).map(inspection => (
                      <div key={inspection.id} className="flex justify-between items-center py-2 border-b last:border-0">
                        <div>
                          <div className="font-medium text-sm">Unit: {inspection.vehicle_unit_number}</div>
                          <div className="text-xs text-gray-500">{inspection.inspection_type?.replace(/_/g, ' ')}</div>
                        </div>
                        <Badge className={inspection.result === 'passed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {inspection.result?.toUpperCase()}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'vehicles' && (
          <Card>
            <CardHeader>
              <CardTitle>Fleet Vehicles</CardTitle>
            </CardHeader>
            <CardContent>
              {vehicles.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Truck className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No vehicles in fleet. Add your first vehicle.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Unit #</th>
                        <th className="text-left py-3 px-2">Type</th>
                        <th className="text-left py-3 px-2">Make/Model</th>
                        <th className="text-left py-3 px-2">Status</th>
                        <th className="text-left py-3 px-2">Last Inspection</th>
                        <th className="text-left py-3 px-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vehicles.map(vehicle => (
                        <tr key={vehicle.id} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-2 font-medium">{vehicle.unit_number}</td>
                          <td className="py-3 px-2">{vehicle.vehicle_type}</td>
                          <td className="py-3 px-2">{vehicle.make} {vehicle.model} {vehicle.year}</td>
                          <td className="py-3 px-2">{getStatusBadge(vehicle.status)}</td>
                          <td className="py-3 px-2 text-xs">{vehicle.last_inspection_date || 'Never'}</td>
                          <td className="py-3 px-2">
                            <Button variant="ghost" size="sm">View</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'issues' && (
          <Card>
            <CardHeader>
              <CardTitle>Open Issues</CardTitle>
            </CardHeader>
            <CardContent>
              {issues.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No open issues.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {issues.map(issue => (
                    <div key={issue.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="font-medium">{issue.title}</div>
                          <div className="text-sm text-gray-500">Unit: {issue.vehicle_unit_number}</div>
                        </div>
                        <div className="flex gap-2">
                          {getPriorityBadge(issue.priority)}
                          <Badge className="bg-gray-100 text-gray-800">{issue.status?.replace(/_/g, ' ')}</Badge>
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">{issue.description}</p>
                      <div className="mt-2 text-xs text-gray-400">
                        Reported by {issue.reported_by_name} on {new Date(issue.reported_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Add Vehicle Modal */}
        {showAddVehicleModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">Add New Vehicle</h3>
              <form onSubmit={handleAddVehicle} className="space-y-4">
                <div>
                  <Label>Unit Number *</Label>
                  <Input
                    value={vehicleForm.unit_number}
                    onChange={(e) => setVehicleForm({...vehicleForm, unit_number: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Vehicle Type *</Label>
                  <select
                    value={vehicleForm.vehicle_type}
                    onChange={(e) => setVehicleForm({...vehicleForm, vehicle_type: e.target.value})}
                    className="w-full border rounded-md p-2"
                  >
                    <option value="truck">Truck</option>
                    <option value="trailer">Trailer</option>
                    <option value="tractor">Tractor</option>
                    <option value="van">Van</option>
                    <option value="flatbed">Flatbed</option>
                    <option value="reefer">Reefer</option>
                  </select>
                </div>
                <div>
                  <Label>VIN</Label>
                  <Input
                    value={vehicleForm.vin}
                    onChange={(e) => setVehicleForm({...vehicleForm, vin: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Make</Label>
                    <Input
                      value={vehicleForm.make}
                      onChange={(e) => setVehicleForm({...vehicleForm, make: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Model</Label>
                    <Input
                      value={vehicleForm.model}
                      onChange={(e) => setVehicleForm({...vehicleForm, model: e.target.value})}
                    />
                  </div>
                </div>
                <div>
                  <Label>Year</Label>
                  <Input
                    type="number"
                    value={vehicleForm.year}
                    onChange={(e) => setVehicleForm({...vehicleForm, year: e.target.value})}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>License Plate</Label>
                    <Input
                      value={vehicleForm.license_plate}
                      onChange={(e) => setVehicleForm({...vehicleForm, license_plate: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>State</Label>
                    <Input
                      value={vehicleForm.license_state}
                      onChange={(e) => setVehicleForm({...vehicleForm, license_state: e.target.value})}
                      maxLength={2}
                    />
                  </div>
                </div>
                <div className="flex gap-3 justify-end pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddVehicleModal(false)}>Cancel</Button>
                  <Button type="submit">Add Vehicle</Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MaintenanceDashboard;
